import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import handleRequest from '../utils/handleRequest';
import {
  SET_PRODUCTS,
  SET_PRODUCTS_FILTERS
} from '../actions/types';
import { GlobalContext } from '../Store';
import { useSnackbar } from 'notistack';

const ProductsContext = React.createContext();

const ProductsProvider = (props) => {
  const { store, dispatch } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();

  const successNotification = (message) =>
    enqueueSnackbar(message, {
      variant: 'success',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });

  const errorNotification = (message) =>
    enqueueSnackbar(message, {
      variant: 'error',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });

  const state = {
    products: store.products || [],
    productsFilters: store.productsFilters,
    templates: store.templates || {},
    removedFeatures: store.removedFeatures || [],
    addedFeatures: store.addedFeatures || [],
  };

  const getProducts = useCallback(async () => {
    const res = await handleRequest({
      method: 'get',
      url: '/products',
    });

    if (res.status === 200) {
      dispatch({
        type: SET_PRODUCTS,
        payload: { products: res.result.products },
      });
    } else {
      // error fetching products
    }
  }, [dispatch]);

  const getFilters = useCallback(async () => {
    const res = await handleRequest({
      method: 'get',
      url: '/products/filters',
    });

    if (res.status === 200) {
      dispatch({
        type: SET_PRODUCTS_FILTERS,
        payload: { productsFilters: res.result.productsFilters },
      });
    } else {
      // error fetching products
    }
  }, [dispatch]);

  const getProductById = useCallback(
    async (id) => {
      const product = state.products.find((product) => product._id === id);

      if (product) {
        return product;
      } else {
        // error product not exist
      }
    },
    [state.products]
  );

  const removeProductById = async (id) => {
    const res = await handleRequest({
      method: 'delete',
      url: `/products/${id}`,
    });

    if (res.status === 200) {
      successNotification('The product removed');
      let products = [...state.products];
      products.splice(
        products.findIndex((e) => e._id === id),
        1
      );
      dispatch({ type: SET_PRODUCTS, payload: { products } });
      return res.result.product;
    } else {
      errorNotification(res.message);
      console.log('failed to remove product. ', res.message);
    }
    return [];
  };

  const removeAllProductsByBU = async (bu) => {
    const res = await handleRequest({
      method: 'delete',
      url: `/products/removeall/${bu}`,
    });

    if (res.status === 200) {
      successNotification('All products for this BU were removed');
      const products = state.products.filter((p) => p.bu !== bu);
      dispatch({ type: SET_PRODUCTS, payload: { products } });
    } else {
      errorNotification(res.message);
      console.log('failed to remove products. ', res.message);
    }
  };

  const getProductByName = async (bu, category, name) => {
    const res = await handleRequest({
      method: 'get',
      url: `/products/${bu}/${category}/${encodeURIComponent(name)}`,
    });

    if (res?.status === 200) {
      return res.result.product;
    } else {
      const errorMessage = res?.message || 'An error occurred while retrieving the product';
      errorNotification(errorMessage);
      console.log('failed to retrieve product. ', errorMessage);
    }
    return [];
  };
  const getProductBySku = async (bu, category, sku) => {
    const res = await handleRequest({
      method: 'get',
      url: `/products/${bu}/${category}/by-sku/${encodeURIComponent(sku)}`,
    });

    if (res?.status === 200) {
      return res.result.product;
    } else {
      const errorMessage = res?.message || 'An error occurred while retrieving the product';
      errorNotification(errorMessage);
      console.log('failed to retrieve product. ', errorMessage);
    }
    return [];
  };

  const getSimilarProductsForId = async (id) => {
    const res = await handleRequest({
      method: 'get',
      url: `/products/${id}/similar`,
    });

    if (res.status === 200) {
      return res.result.products;
    } else {
      const errorMessage = res?.message || 'An error occurred while retrieving the product';
      errorNotification(errorMessage);
      console.log('failed to retrieve product. ', errorMessage);
    }
    return [];
  };

  const createProduct = useCallback(
    async (bu, category, data, onSuccess) => {
      const res = await handleRequest({
        method: 'post',
        url: `/products/${bu}/${category}`,
        data,
      }).catch(res => {
        errorNotification(res.message);
        console.log('failed to create product. ', res.message);
      });

      if (res.status === 200) {
        // const products = [...state.products, res.result.product];
        typeof onSuccess === 'function' && onSuccess();
      } else {
        errorNotification(res.message);
        console.log('failed to create product. ', res.message);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [state.products, dispatch]
  );

  const updateProduct = useCallback(
    async (id, bu, category, data, onSuccess) => {
      const res = await handleRequest({
        method: 'put',
        url: `/products/${bu}/${category}/${id}`,
        data,
      });

      if (res?.status === 200) {
        typeof onSuccess === 'function' && onSuccess();
      } else {
        errorNotification(res?.message);
        console.log('failed to edit product. ', res?.message);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [state.products, dispatch]
  );

  const saveProductDatasheet = useCallback(
    async (productName, templateId, data, onSuccess) => {
      const prodNameInUrl = encodeURIComponent(productName)
      const res = await handleRequest({
        method: 'put',
        url: `/products/${prodNameInUrl}/pdf/${templateId}`,
        data,
      });

      if (res?.status === 200) {
        typeof onSuccess === 'function' && onSuccess();
      } else {
        errorNotification(res?.message);
        console.log('failed to edit product. ', res?.message);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch]
  );

  const approveProductDatasheet = useCallback(
    async (productName, templateId, data, onSuccess) => {
      const res = await handleRequest({
        method: 'patch',
        url: `/products/${productName}/pdf/${templateId}`,
        data,
      });

      if (res.status === 200) {
        typeof onSuccess === 'function' && onSuccess();
      } else {
        errorNotification(res.message);
        console.log('failed to approve product. ', res.message);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch]
  );

  const actions = {
    getProducts,
    getFilters,
    getProductById,
    getProductByName,
    getProductBySku,
    createProduct,
    updateProduct,
    removeProductById,
    removeAllProductsByBU,
    getSimilarProductsForId,
    saveProductDatasheet,
    approveProductDatasheet
  };

  return (
    <ProductsContext.Provider value={{ state, actions }}>
      {props.children}
    </ProductsContext.Provider>
  );
};

ProductsContext.propTypes = {
  children: PropTypes.element,
};

export { ProductsProvider, ProductsContext };
